/**
 * Author: Romain Hapi Sr.
 * Version: 2.0
 * Date: 2026-09-10
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Realisations du site Tchad.
 *
 * Les projets viennent de `mockData` : ils sont stables et n'ont pas de route
 * backend. Le filtre par categorie est applique cote client.
 */
import React, { useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { PageHero } from '../components/Section';
import { ProjectCard } from '../components/ProjectCard';
import { useReveal } from '../hooks/useReveal';
import SEO from '../../components/SEO';
import { mockProjects } from '../mockData';
import { SHELL, SECTION } from '../theme';
import ProductCards from '../../components/ProductCards';
import { PRODUCT_UI, tx } from '../../lib/products';

const META = {
  fr: {
    title: 'Projets — Prenim Tchad',
    description:
      "Realisations de Prenim Tchad : plateformes, applications et infrastructures livrees au Tchad et en zone CEMAC.",
  },
  en: {
    title: 'Projects — Prenim Tchad',
    description:
      'Work delivered by Prenim Tchad: platforms, applications and infrastructure across Chad and the CEMAC area.',
  },
  ar: {
    title: 'المشاريع — Prenim Tchad',
    description: 'أعمال أنجزتها Prenim Tchad: منصات وتطبيقات وبنى تحتية في تشاد ومنطقة CEMAC.',
  },
};

const EYEBROW = { fr: 'References', en: 'Selected work', ar: 'أعمال مختارة' };
const ALL = { fr: 'Tous', en: 'All', ar: 'الكل' };
const EMPTY = {
  fr: 'Aucun projet dans cette categorie.',
  en: 'No project in this category.',
  ar: 'لا يوجد مشروع في هذه الفئة.',
};

export const Projects = () => {
  const { t, language } = useLanguage();
  const [filter, setFilter] = useState('all');
  // Contenu local : `/api/projects` n'existe pas cote backend (404).
  const projects = mockProjects;
  const grid = useReveal();

  // Les categories etaient codees en dur, dont plusieurs sans aucun projet :
  // le visiteur cliquait sur un filtre qui ne donnait rien. Elles sont
  // desormais deduites des projets reellement presents.
  const categories = useMemo(() => {
    const seen = projects.map((p) => p.category).filter(Boolean);
    return ['all', ...Array.from(new Set(seen))];
  }, [projects]);

  const filtered =
    filter === 'all' ? projects : projects.filter((p) => p.category === filter);

  const meta = META[language] || META.fr;

  return (
    <main data-testid="tchad-projects-page">
      <SEO title={meta.title} description={meta.description} lang={language} />

      <PageHero
        eyebrow={EYEBROW[language] || EYEBROW.fr}
        title={t.projects.title}
        lead={t.projects.subtitle}
      />

      {/* Logiciels Prenim : les cartes menent aux pages produits du site UK,
          qui portent les videos et les documents. */}
      <section className={SECTION}>
        <div className={SHELL}>
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-amber-600">
              {tx(PRODUCT_UI.eyebrow, language)}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">
              {tx(PRODUCT_UI.sectionTitle, language)}
            </h2>
            <p className="mt-4 text-lg text-slate-600">{tx(PRODUCT_UI.sectionSubtitle, language)}</p>
          </div>
          <ProductCards lang={language} />
        </div>
      </section>

      <section className={`${SECTION} bg-slate-50`}>
        <div className={SHELL}>
          {/* Filtres : de vrais boutons, avec `aria-pressed`, plutot que des
              pastilles decoratives. */}
          <div className="flex flex-wrap gap-2" role="group" aria-label={t.projects.title}>
            {categories.map((category) => {
              const active = filter === category;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => setFilter(category)}
                  aria-pressed={active}
                  data-testid={`tchad-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
                    active
                      ? 'border-slate-950 bg-slate-950 text-white'
                      : 'border-slate-300 bg-white text-slate-700 hover:border-slate-950 hover:text-slate-950'
                  }`}
                >
                  {category === 'all' ? ALL[language] || ALL.fr : category}
                </button>
              );
            })}
          </div>

          {filtered.length ? (
            <div
              ref={grid.ref}
              className={`${grid.className} mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3`}
            >
              {filtered.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  language={language}
                  viewLabel={t.projects.viewDetails}
                />
              ))}
            </div>
          ) : (
            <p className="no-justify mt-12 text-slate-600">{EMPTY[language] || EMPTY.fr}</p>
          )}
        </div>
      </section>
    </main>
  );
};
